/**
 * AssignmentManagerModal Component
 * Modal for managing job recruiter assignments
 */

import React, { useState, useEffect } from 'react'
import { XMarkIcon, UserMinusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { usersApi } from '../../services/api'
import type { JobAssignment } from '../../services/api'
import { getInitials, getColor } from './AssignedRecruitersDisplay'
import { usePermission } from '../../hooks/usePermission'

interface AssignmentManagerModalProps {
  isOpen: boolean
  onClose: () => void
  jobId: string
  jobTitle: string
  currentAssignments: JobAssignment[]
  onSave: (userIds: string[]) => Promise<void>
  isLoading?: boolean
}

interface User {
  id: string
  username: string
  email: string
  first_name: string
  last_name: string
  role: string
}

export const AssignmentManagerModal: React.FC<AssignmentManagerModalProps> = ({
  isOpen,
  onClose,
  jobId,
  jobTitle,
  currentAssignments,
  onSave,
  isLoading = false,
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)

  // Gate mismatch fix (Priority 4 audit) — this fetch has always been
  // gated on users.viewRoster on the backend (gated("users.viewRoster",
  // ...), users.py's get_all_recruiters), but had no matching frontend
  // check. No practical access change: every role that can reach this
  // modal at all (jobs.manageRecruiters — admin/manager/senior_recruiter)
  // already ranks at or above users.viewRoster's recruiter-tier grant.
  const { can } = usePermission()

  // Fetch all recruiters (no pagination)
  const { data: usersData } = useQuery({
    queryKey: ['users', 'all-recruiters'],
    queryFn: usersApi.getAllRecruiters,
    enabled: isOpen && can('users.viewRoster'),
  })

  const allUsers: User[] = usersData?.data || []

  // Initialize selected user IDs from current assignments
  useEffect(() => {
    if (isOpen) {
      setSelectedUserIds(currentAssignments.map(a => a.id))
      setSearchQuery('')
      setShowDropdown(false)
    }
  }, [isOpen, currentAssignments])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.search-dropdown-container')) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  if (!isOpen) return null

  const handleAddUser = (user: User) => {
    if (!selectedUserIds.includes(user.id)) {
      setSelectedUserIds([...selectedUserIds, user.id])
    }
    setSearchQuery('')
    setShowDropdown(false)
  }

  const handleRemoveUser = (userId: string) => {
    setSelectedUserIds(selectedUserIds.filter(id => id !== userId))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave(selectedUserIds)
      onClose()
    } catch (error) {
      console.error('Failed to save assignments:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    // Reset to original assignments
    setSelectedUserIds(currentAssignments.map(a => a.id))
    onClose()
  }

  // Get full assignment details for selected users
  // Combine existing assignments with newly selected users from allUsers
  const selectedAssignments = selectedUserIds.map(userId => {
    // First check if it's an existing assignment
    const existingAssignment = currentAssignments.find(a => a.id === userId)
    if (existingAssignment) {
      return existingAssignment
    }
    
    // Otherwise, find it in allUsers and create assignment object
    const user = allUsers.find(u => u.id === userId)
    if (user) {
      return {
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        role: user.role,
      }
    }
    return null
  }).filter((a): a is JobAssignment => a !== null)

  // Filter available users (not already selected)
  const availableUsers = allUsers.filter(user => 
    !selectedUserIds.includes(user.id) &&
    (searchQuery === '' || 
     `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
     user.email.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  const hasChanges = JSON.stringify(selectedUserIds.sort()) !== 
                     JSON.stringify(currentAssignments.map(a => a.id).sort())

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Manage Assigned Recruiters</h3>
            <p className="text-sm text-gray-600 mt-1">{jobTitle}</p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSaving}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Add Recruiter Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add Recruiter
            </label>
            <div className="relative search-dropdown-container">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setShowDropdown(true)
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Dropdown */}
              {showDropdown && searchQuery && availableUsers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {availableUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleAddUser(user)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                    >
                      <div
                        className={`
                          w-8 h-8 rounded-full flex items-center justify-center
                          text-white font-semibold text-xs
                          ${getColor(`${user.first_name} ${user.last_name}`)}
                        `}
                      >
                        {getInitials(`${user.first_name} ${user.last_name}`)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* No results message */}
              {showDropdown && searchQuery && availableUsers.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
                  No recruiters found matching "{searchQuery}"
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Search by name or email to add a recruiter to this job
            </p>
          </div>

          {/* Current Assignments Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Assigned Recruiters ({selectedAssignments.length})
            </label>
            
            {selectedAssignments.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 text-sm">No recruiters assigned</p>
                <p className="text-gray-400 text-xs mt-1">Add recruiters using the search above</p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      {/* Avatar */}
                      <div
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center
                          text-white font-semibold text-sm
                          ${getColor(assignment.name)}
                        `}
                      >
                        {getInitials(assignment.name)}
                      </div>
                      
                      {/* User Info */}
                      <div>
                        <div className="font-medium text-gray-900">{assignment.name}</div>
                        <div className="text-sm text-gray-500">{assignment.email}</div>
                        <div className="text-xs text-gray-400 capitalize">{assignment.role}</div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemoveUser(assignment.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove recruiter"
                      disabled={isSaving}
                    >
                      <UserMinusIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`
              px-4 py-2 rounded-lg font-medium transition-colors
              ${hasChanges && !isSaving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Saving...</span>
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
